/**
 * La comunicazione delle scadenze da mandare al cliente.
 *
 * Si sceglie l'orizzonte, si guarda cosa conterrà e poi si scarica: sono dati
 * del personale di un'azienda terza, e mandarli senza averli letti è il modo
 * più semplice per accorgersi troppo tardi di un destinatario sbagliato o di
 * un nominativo che non doveva esserci.
 */
import { useEffect, useState } from 'react';
import { formazioneApi } from '../api';
import Modal from './Modal';

const eur = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

export default function ComunicaScadenze({ clienteId, nomeCliente, onClose }) {
  const [giorni, setGiorni] = useState(90);
  const [conScaduti, setConScaduti] = useState(true);
  const [referente, setReferente] = useState('');
  const [dati, setDati] = useState(null);
  const [lavorando, setLavorando] = useState(false);
  const [errore, setErrore] = useState('');

  useEffect(() => {
    let vivo = true;
    setDati(null);
    formazioneApi.anteprimaComunicazione(clienteId, giorni, conScaduti)
      .then(d => { if (vivo) setDati(d); })
      .catch(e => setErrore(String(e?.error || e)));
    return () => { vivo = false; };
  }, [clienteId, giorni, conScaduti]);

  const scarica = async (formato) => {
    setLavorando(true); setErrore('');
    try { await formazioneApi.scaricaComunicazione(clienteId, { giorni, conScaduti, referente }, formato, nomeCliente); }
    catch (e) { setErrore(String(e?.error || e)); } finally { setLavorando(false); }
  };

  const scadute = (dati?.scadenze || []).filter(s => s.giorni < 0).length;
  const prossime = (dati?.scadenze || []).length - scadute;
  const stima = (dati?.proposta || []).reduce(
    (t, c) => t + (Number(c.prezzo_persona) || 0) * c.persone, 0);

  return (
    <Modal title={`Comunicazione scadenze — ${nomeCliente || ''}`} onClose={onClose} size="lg">
      <div className="space-y-4 text-sm">
        <div className="flex gap-3 flex-wrap items-end">
          <div>
            <label className="label">Orizzonte</label>
            <select className="input py-1 text-sm w-44" value={giorni}
                    onChange={e => setGiorni(Number(e.target.value))}>
              {[30, 60, 90, 180, 365].map(g => (
                <option key={g} value={g}>Prossimi {g} giorni</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 pb-1.5">
            <input type="checkbox" className="w-4 h-4" checked={conScaduti}
                   onChange={e => setConScaduti(e.target.checked)} />
            Includi quelli già scaduti
          </label>
          <div className="flex-1 min-w-[180px]">
            <label className="label">Alla cortese attenzione di</label>
            <input className="input py-1 text-sm" placeholder="facoltativo"
                   value={referente} onChange={e => setReferente(e.target.value)} />
          </div>
        </div>

        {!dati && !errore && <p className="text-xs text-gray-400">Preparo l'anteprima…</p>}
        {errore && (
          <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700">{errore}</div>
        )}

        {dati && (
          <>
            <div className="grid grid-cols-3 gap-2">
              {[['Già scaduti', scadute, 'text-red-700'],
                ['In scadenza', prossime, 'text-amber-800'],
                ['Corsi proponibili', dati.proposta.length, 'text-brand-700']].map(([t, n, c]) => (
                <div key={t} className="border rounded-lg px-3 py-2">
                  <div className="text-[11px] text-gray-500 uppercase tracking-wide">{t}</div>
                  <div className={`text-lg font-semibold ${n > 0 ? c : 'text-gray-300'}`}>{n}</div>
                </div>
              ))}
            </div>

            {dati.proposta.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <div className="px-3 py-1.5 bg-gray-50 border-b text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Proposta che accompagnerà le scadenze
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {dati.proposta.map(c => (
                      <tr key={c.id} className="border-b last:border-0">
                        <td className="px-3 py-1.5">{c.nome}</td>
                        <td className="px-3 py-1.5 text-gray-500">{c.persone} persone</td>
                        <td className="px-3 py-1.5 text-right">
                          {c.prezzo_persona ? `${eur(c.prezzo_persona)}/persona` : '—'}
                        </td>
                      </tr>
                    ))}
                    {stima > 0 && (
                      <tr className="bg-gray-50">
                        <td className="px-3 py-1.5 font-medium" colSpan={2}>Stima complessiva</td>
                        <td className="px-3 py-1.5 text-right font-semibold text-brand-700">{eur(stima)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-amber-700">
                Nessun corso del nostro catalogo è abbinato a queste scadenze: la comunicazione
                uscirà con le sole scadenze. Gli abbinamenti si fanno in <strong>Corsi previsti</strong>.
              </p>
            )}

            {/* Quello che resta fuori dalla proposta è un'occasione mancata:
                vale la pena saperlo prima di mandare. */}
            {dati.senza_proposta?.length > 0 && (
              <p className="text-xs text-gray-500">
                Senza un corso abbinato nel nostro catalogo: {dati.senza_proposta.join(', ')}.
              </p>
            )}
          </>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button className="btn-secondary" onClick={onClose}>Chiudi</button>
        <button className="btn-secondary" disabled={!dati || lavorando}
                onClick={() => scarica('docx')}>⬇ Word</button>
        <button className="btn-primary" disabled={!dati || lavorando}
                onClick={() => scarica('pdf')}>{lavorando ? '…' : '⬇ PDF'}</button>
      </div>
    </Modal>
  );
}
